import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Music } from 'lucide-react';
import { useApp } from '../context/AppContext';

const AudioPlayer = () => {
  const { currentBeat, isPlaying, setIsPlaying } = useApp();
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const lastBeatId = useRef(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  useEffect(() => {
    if (currentBeat && audioRef.current) {
      // Only reload if it's a different beat
      if (lastBeatId.current !== currentBeat.beat_id) {
        lastBeatId.current = currentBeat.beat_id;
        
        // Get the audio URL, preferring preview_url over audio_url
        let audioUrl = currentBeat.preview_url || currentBeat.audio_url;
        
        // If the URL is relative (starts with /api), prepend the backend URL
        if (audioUrl && audioUrl.startsWith('/api')) {
          audioUrl = process.env.REACT_APP_BACKEND_URL + audioUrl;
        }
        
        // Set source and play immediately
        audioRef.current.src = audioUrl;
        audioRef.current.load(); // Force load
        
        if (isPlaying) {
          // Play as soon as we have enough data
          const playWhenReady = () => {
            audioRef.current.play().catch(e => console.error('Play error:', e));
            audioRef.current.removeEventListener('canplay', playWhenReady);
          };
          audioRef.current.addEventListener('canplay', playWhenReady);
          // Also try to play immediately in case already loaded
          audioRef.current.play().catch(() => {});
        }
      } else if (isPlaying) {
        // Same beat, just play/resume
        audioRef.current.play().catch(e => console.error('Play error:', e));
      }
    }
  }, [currentBeat, isPlaying]);

  useEffect(() => {
    if (audioRef.current && currentBeat) {
      if (isPlaying) {
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(prog);
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    const seekTime = (x / width) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!currentBeat) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-[#D4AF37]/20" data-testid="audio-player">
      <audio
        ref={audioRef}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
        onCanPlayThrough={() => {
          // Auto-play when ready if isPlaying is true
          if (isPlaying && audioRef.current.paused) {
            audioRef.current.play().catch(() => {});
          }
        }}
      />
      
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-6">
          {/* Beat info */}
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-800 flex-shrink-0 border border-[#D4AF37]/20">
              {currentBeat.image_url ? (
                <img src={currentBeat.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Music size={24} className="text-[#D4AF37]/50" />
                </div>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate text-white" style={{ fontFamily: 'Syne' }}>{currentBeat.title}</p>
              <p className="text-sm text-neutral-500 mono">{currentBeat.bpm} BPM • {currentBeat.key}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8941F] flex items-center justify-center glow-primary hover:scale-105 transition-transform"
              data-testid="play-pause-btn"
            >
              {isPlaying ? <Pause size={20} fill="black" stroke="black" /> : <Play size={20} fill="black" stroke="black" className="ml-0.5" />}
            </button>
          </div>

          {/* Progress */}
          <div className="hidden md:flex items-center gap-3 flex-1">
            <span className="text-xs text-neutral-500 mono w-10">{formatTime(currentTime)}</span>
            <div 
              className="flex-1 h-1.5 bg-neutral-800 rounded-full cursor-pointer group"
              onClick={handleSeek}
              data-testid="progress-bar"
            >
              <div 
                className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] rounded-full relative"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#FFD700] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </div>
            <span className="text-xs text-neutral-500 mono w-10">{formatTime(duration)}</span>
          </div>

          {/* Volume */}
          <div className="hidden lg:flex items-center gap-2">
            <button onClick={() => setMuted(!muted)} className="p-2 text-neutral-400 hover:text-[#D4AF37]">
              {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
              className="w-20 accent-[#D4AF37]"
              data-testid="volume-slider"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
